import { useState, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

const WEBHOOK_URL = "https://api.stero.ai/webhooks/whatsapp/inbound";
const STEP_COUNT = 4;
const INPUT_CLS = "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";
const BTN_PRI = "rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50";
const BTN_SEC = "rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50";
const SUCCESS_CLS = "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
const ERROR_CLS = "border-destructive/30 bg-destructive/10 text-destructive";

type WhatsAppProvider = "360dialog" | "meta";

function extractError(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: STEP_COUNT }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${i < current ? "bg-[var(--color-success)]/10 text-[var(--color-success)]" : i === current ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
            {i < current ? "\u2713" : i + 1}
          </div>
          {i < STEP_COUNT - 1 && <div className={`h-px w-8 ${i < current ? "bg-[var(--color-success)]" : "bg-border"}`} />}
        </div>
      ))}
    </div>
  );
}

export default function WhatsAppConfigWizard() {
  const [step, setStep] = useState(0);
  const [provider, setProvider] = useState<WhatsAppProvider>("360dialog");
  const [credential, setCredential] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ ok: boolean; message: string } | null>(null);
  const credentialLabel = provider === "360dialog" ? "360dialog API Key" : "Meta Access Token";
  const credentialPlaceholder = provider === "360dialog"
    ? "Enter your 360dialog API key"
    : "Enter your Meta Graph API access token";

  const handleVerify = useCallback(async () => {
    setIsVerifying(true);
    setVerifyResult(null);
    try {
      const result = await apiFetch<{
        valid: boolean;
        phoneNumbers?: Array<{ id: string; number: string; displayName: string }>;
        error?: string;
      }>("/api/messaging/whatsapp/validate", {
        method: "POST",
        body: JSON.stringify(provider === "360dialog"
          ? { provider, apiKey: credential }
          : { provider, accessToken: credential }),
      });
      if (!result.valid) {
        setVerifyResult({ ok: false, message: result.error ?? "Verification failed" });
        return;
      }

      const firstPhoneNumber = result.phoneNumbers?.[0];
      setPhoneNumber(firstPhoneNumber?.number ?? "");
      setPhoneNumberId(firstPhoneNumber?.id ?? "");
      setVerifyResult({
        ok: true,
        message: firstPhoneNumber
          ? `Connected. Phone: ${firstPhoneNumber.number}`
          : "Connected. No phone numbers were returned by the provider.",
      });
    } catch (err: unknown) {
      setVerifyResult({ ok: false, message: extractError(err, "Verification failed") });
    } finally { setIsVerifying(false); }
  }, [credential, provider]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveResult(null);
    try {
      await apiFetch("/api/messaging/whatsapp/config", {
        method: "PUT",
        body: JSON.stringify({
          provider,
          ...(provider === "360dialog" ? { apiKey: credential } : { accessToken: credential }),
          phoneNumber,
          phoneNumberId: phoneNumberId || undefined,
        }),
      });
      setSaveResult({ ok: true, message: "Configuration saved successfully" });
    } catch (err: unknown) {
      setSaveResult({ ok: false, message: extractError(err, "Configuration save failed") });
    } finally { setIsSaving(false); }
  }, [credential, phoneNumber, phoneNumberId, provider]);

  const canAdvance = step === 0
    ? credential.length > 0
    : step === 1
      ? verifyResult?.ok === true
      : step === 2
        ? phoneNumber.length > 0
        : true;

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">WhatsApp Business Setup</h2>
        <p className="mt-1 text-sm text-muted-foreground">Connect your WhatsApp Business account via 360dialog or direct Meta Graph API.</p>
      </div>
      <div className="rounded-md border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-3 text-xs text-[var(--color-warning)]">
        Messages outside the 24-hour customer window require an approved template.
      </div>
      <StepIndicator current={step} />

      {step === 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Choose provider and credential</h3>
          <label className="block space-y-2">
            <span className="text-xs font-medium text-muted-foreground">Provider</span>
            <select
              name="provider"
              value={provider}
              onChange={(e) => {
                setProvider(e.target.value as WhatsAppProvider);
                setCredential("");
                setPhoneNumber("");
                setPhoneNumberId("");
                setVerifyResult(null);
                setSaveResult(null);
              }}
              className={INPUT_CLS}
            >
              <option value="360dialog">360dialog</option>
              <option value="meta">Direct Meta Graph API</option>
            </select>
          </label>
          <label className="block space-y-2">
            <span className="text-xs font-medium text-muted-foreground">{credentialLabel}</span>
            <input
              name="whatsappCredential"
              type="password"
              value={credential}
              onChange={(e) => setCredential(e.target.value)}
              placeholder={credentialPlaceholder}
              className={INPUT_CLS}
            />
          </label>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Verify Connection</h3>
          <p className="text-sm text-muted-foreground">We will verify your {credentialLabel.toLowerCase()} and retrieve your phone number.</p>
          <button onClick={handleVerify} disabled={isVerifying} className={BTN_SEC}>{isVerifying ? "Verifying..." : "Verify Connection"}</button>
          {verifyResult && <div className={`rounded-md border p-3 text-sm ${verifyResult.ok ? SUCCESS_CLS : ERROR_CLS}`}>{verifyResult.message}</div>}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Webhook URL</h3>
          <p className="text-sm text-muted-foreground">This webhook URL is auto-configured. No action needed.</p>
          <div className="rounded-md border border-border bg-muted/30 p-4">
            <p className="text-xs text-muted-foreground">Webhook URL</p>
            <p className="mt-1 break-all font-mono text-sm text-foreground">{WEBHOOK_URL}</p>
          </div>
          <label className="block space-y-2">
            <span className="text-xs font-medium text-muted-foreground">Connected Phone Number</span>
            <input
              name="phoneNumber"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+49123456789"
              className={INPUT_CLS}
            />
          </label>
          {phoneNumberId ? (
            <div className="rounded-md border border-border bg-muted/30 p-4">
              <p className="text-xs text-muted-foreground">Phone Number ID</p>
              <p className="mt-1 text-sm font-medium text-foreground">{phoneNumberId}</p>
            </div>
          ) : null}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Save Configuration</h3>
          <p className="text-sm text-muted-foreground">
            Persist the verified {provider === "360dialog" ? "360dialog" : "direct Meta"} WhatsApp provider configuration for this tenant.
          </p>
          <button onClick={handleSave} disabled={isSaving} className={BTN_PRI}>{isSaving ? "Saving..." : "Save Configuration"}</button>
          {saveResult && <div className={`rounded-md border p-3 text-sm ${saveResult.ok ? SUCCESS_CLS : ERROR_CLS}`}>{saveResult.message}</div>}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-border pt-4">
        <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50">Back</button>
        {step < STEP_COUNT - 1 && <button onClick={() => setStep((s) => Math.min(STEP_COUNT - 1, s + 1))} disabled={!canAdvance} className={BTN_PRI}>Next</button>}
      </div>
    </div>
  );
}
